import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { uid } from '../../../../lib/uid';
import type { EmploymentType } from '../personnelTypes';

/**
 * Test data for the work experience form's Autogenerate.
 *
 * **Nothing here is resolved against a catalog**, unlike `sampleEligibility`, which must pick
 * a real row from the loaded eligibility list or the server refuses the id. A work-experience
 * row has no foreign keys at all, so every value is simply made up — which is exactly the
 * property the module was built for, showing up in its generator.
 *
 * The one rule that still bites: the values must satisfy the form. A start date in the future
 * or an end date before the start is refused server-side, so the dates are generated as a real
 * span in the past.
 */
export interface SampleWorkExperience {
  periodFrom: string;
  periodTo: string;
  positionTitle: string;
  agencyName: string;
  monthlySalary: string;
  salaryGrade: string;
  stepIncrement: string;
  appointmentStatus: EmploymentType;
  isGovernmentService: 'yes' | 'no';
}

export function sampleWorkExperience(): SampleWorkExperience {
  const tag = uid().slice(0, 6).toUpperCase();

  /*
    Calendar dates built from parts, never `toISOString` — that converts through UTC and can
    hand the field the day before the one intended.
  */
  const today = todayCalendarDate();
  const [year, month, day] = today.split('-').map(Number);
  const pad = (n: number) => String(n).padStart(2, '0');

  // A span that ended before today and began before it ended, which is every date rule this
  // form has. Two to eight years back, running for two of them.
  const startYear = year - 4 - Math.floor(Math.random() * 5);
  const endYear = startYear + 2;

  /*
    Government service half the time, because both branches of the form are worth exercising:
    the column is the one anybody aggregates on, and a generator that only ever produced "yes"
    would leave the private-employment case untested by every click of the button.
  */
  const government = Math.random() < 0.5;

  return {
    periodFrom: `${startYear}-${pad(month)}-${pad(day)}`,
    periodTo: `${endYear}-${pad(month)}-${pad(day)}`,

    // A plausible title, which is what every other generator in this module does for ordinary
    // vocabulary. The obviously-synthetic rule is about values that land in the identity
    // store, and nothing on this row does — it names an employer, never a person.
    positionTitle: 'Administrative Officer II',
    agencyName: `Test Agency ${tag}`,

    // A figure, not a rate read from the salary schedule — that independence is the point of
    // the module, and it is the point here too.
    monthlySalary: String(20000 + Math.floor(Math.random() * 30000)),
    salaryGrade: String(10 + Math.floor(Math.random() * 15)),
    stepIncrement: String(1 + Math.floor(Math.random() * 8)),

    // Permanent — the form's first listed status and the commonest real answer.
    appointmentStatus: 1,
    isGovernmentService: government ? 'yes' : 'no',
  };
}
